import React, { useState, useEffect } from 'react';
import { getDevices, getDrivers, updateDriver } from '../traccar';
import {
  getLoads, saveLoad, deleteLoad, emptyLoad, loadTotal, chargeAmount,
  LOAD_STATUSES, STATUS_COLORS, getBillSettings, saveBillSettings,
  getParties, rememberParty, findPartyByAccount,
  getDetentionSettings, saveDetentionSettings,
  emptyStop, syncStops, ensureStops,
} from '../loadStore';
import { playOpen, playClose } from '../clickSound';
import { parseRateCon, pdfToText } from '../rateConParser';
import AddressAutocomplete from '../AddressAutocomplete';
import { billingChecklist } from '../sop';

const inp = { padding: '7px 9px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', width: '100%', background: '#ffffff' };
const lbl = { fontSize: '10px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.4px', display: 'block', marginBottom: '3px' };
const btnBlue = { padding: '8px 16px', borderRadius: '6px', fontWeight: 700, fontSize: '13px', background: 'linear-gradient(135deg, #0066ff, #0047d0)', color: 'white' };
const btnOrange = { ...btnBlue, background: 'linear-gradient(135deg, #f97316, #f4511e)' };
const money = (n) => `$${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : '—');
const num = (v) => (Number(v) || 0);

// TruckMate-style tabs across the top of a load / bill
const LOAD_TABS = ['Summary', 'Stops', 'Details', 'Charges', 'Accessorials', 'Cost', 'Driver Pay', 'Documents', 'Invoice', 'A/R', 'Dispatch', 'Audit', 'Margin'];
const ACCESSORIAL_CODES = [['DET', 'Detention'], ['LUMP', 'Lumper Fee'], ['LAY', 'Layover'], ['DASST', 'Driver Assist'], ['STOP', 'Extra Stop'], ['LIFT', 'Liftgate'], ['RESI', 'Residential Delivery'], ['APPT', 'Appointment Fee'], ['REEFER', 'Reefer Surcharge'], ['HAZ', 'Hazmat Fee'], ['TRLDET', 'Trailer Detention'], ['DOC', 'Document Fee'], ['PODF', 'POD Fee'], ['WKND', 'Weekend Delivery']];
const DOC_LIST = [['rateCon', 'Rate Confirmation'], ['revisedRateCon', 'Revised Rate Con (lumper)'], ['bol', 'Bill of Lading'], ['pod', 'POD'], ['lumperReceipt', 'Lumper Receipt'], ['fuelReceipt', 'Fuel Receipt'], ['scaleTicket', 'Scale Ticket'], ['invoiceCopy', 'Invoice Copy'], ['accApproval', 'Accessorial Approval']];
const sec = { border: '1px solid #e2e8f0', borderRadius: '10px', padding: '12px', marginBottom: '12px', background: '#f8fafc' };

const StatusPill = ({ s }) => (
  <span style={{ fontSize: '11px', fontWeight: 700, padding: '3px 10px', borderRadius: '999px', background: (STATUS_COLORS[s] || '#64748b') + '22', color: STATUS_COLORS[s] || '#64748b' }}>{s}</span>
);

// a party (caller / shipper / consignee) editor block
function PartyBlock({ title, party, onChange, directory, listId, onSaveProfile }) {
  const set = (k, v) => onChange({ ...party, [k]: v });

  // typing/leaving the company field: if it matches a saved profile, offer to fill
  const fillFromRecord = (rec) => onChange({ ...party, name: rec.name, account: rec.account, address: rec.address, city: rec.city, state: rec.state, zip: rec.zip, phone: rec.phone, contact: rec.contact });

  const lookupAccount = async () => {
    if (!party.account) { alert('Enter an account number first.'); return; }
    const rec = await findPartyByAccount(party.account);
    if (rec) fillFromRecord(rec);
    else alert(`No saved profile with account #${party.account}.`);
  };

  const onCompanyChange = (v) => {
    // if they picked an existing company from the datalist, autofill the rest
    const match = directory.find((d) => d.name.toLowerCase() === v.toLowerCase());
    if (match) fillFromRecord(match);
    else set('name', v);
  };

  return (
    <div style={{ border: '1px solid #dbeafe', borderLeft: '4px solid #0066ff', background: '#eff6ff', borderRadius: '10px', padding: '12px' }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
        <div style={{ fontFamily: 'Poppins, sans-serif', fontWeight: 700, fontSize: '13px', color: '#0f172a', flex: 1 }}>{title}</div>
        <button onClick={onSaveProfile} title="Save this as a reusable profile" style={{ background: '#dbeafe', color: '#1d4ed8', fontSize: '11px', fontWeight: 700, padding: '3px 8px', borderRadius: '5px' }}>💾 Save profile</button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '8px' }}>
        <div style={{ minWidth: 0 }}>
          <span style={lbl}>Company</span>
          <input style={inp} list={listId} value={party.name} onChange={(e) => onCompanyChange(e.target.value)} placeholder="type or pick saved…" />
          <datalist id={listId}>
            {directory.map((d) => <option key={d.key} value={d.name}>{d.account ? `#${d.account}` : ''}</option>)}
          </datalist>
        </div>
        <div style={{ minWidth: 0 }}>
          <span style={lbl}>Account #</span>
          <div style={{ display: 'flex', gap: '4px' }}>
            <input style={{ ...inp, minWidth: 0 }} value={party.account} onChange={(e) => set('account', e.target.value)} />
            <button onClick={lookupAccount} title="Fill from account #" style={{ background: '#0066ff', color: 'white', borderRadius: '5px', padding: '0 9px', fontWeight: 700, flexShrink: 0 }}>↵</button>
          </div>
        </div>
      </div>
      <div style={{ marginTop: '6px' }}><span style={lbl}>Address</span>
        <AddressAutocomplete style={inp} value={party.address}
          onChange={(v) => set('address', v)}
          onPickParts={(p) => onChange({ ...party, address: p.street || p.description, city: p.city || party.city, state: p.state || party.state, zip: p.zip || party.zip })} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '8px', marginTop: '6px' }}>
        <div><span style={lbl}>City</span><input style={inp} value={party.city} onChange={(e) => set('city', e.target.value)} /></div>
        <div><span style={lbl}>State</span><input style={inp} value={party.state} onChange={(e) => set('state', e.target.value)} /></div>
        <div><span style={lbl}>Zip</span><input style={inp} value={party.zip} onChange={(e) => set('zip', e.target.value)} /></div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginTop: '6px' }}>
        <div><span style={lbl}>Phone</span><input style={inp} value={party.phone} onChange={(e) => set('phone', e.target.value)} /></div>
        <div><span style={lbl}>Contact</span><input style={inp} value={party.contact} onChange={(e) => set('contact', e.target.value)} /></div>
      </div>
    </div>
  );
}

// one stop (a pickup or a delivery) in the load's stop list
function StopBlock({ stop, index, count, onChange, onRemove, onAddSame, directory }) {
  const set = (k, v) => onChange({ ...stop, [k]: v });
  const isPickup = stop.type === 'pickup';
  const fillFrom = (rec) => onChange({ ...stop, name: rec.name, account: rec.account, address: rec.address, city: rec.city, state: rec.state, zip: rec.zip, phone: rec.phone, contact: rec.contact });
  const onCompany = (v) => { const m = directory.find((d) => d.name.toLowerCase() === v.toLowerCase()); if (m) fillFrom(m); else set('name', v); };
  const listId = `stopdir-${stop.id}`;
  return (
    <div style={{ border: `1px solid ${isPickup ? '#bbf7d0' : '#fecaca'}`, borderLeft: `4px solid ${isPickup ? '#16a34a' : '#dc2626'}`, background: isPickup ? '#f0fdf4' : '#fef2f2', borderRadius: '10px', padding: '12px', marginBottom: '10px' }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
        <span style={{ fontFamily: 'Poppins, sans-serif', fontWeight: 700, fontSize: '13px', color: '#0f172a', flex: 1 }}>
          {isPickup ? '🚚 Pickup' : '🏁 Delivery'} #{index}
        </span>
        {count > 1 && <button onClick={onRemove} style={{ background: '#fef2f2', color: '#b91c1c', fontSize: '11px', fontWeight: 700, padding: '3px 9px', borderRadius: '5px' }}>Remove</button>}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '8px' }}>
        <div style={{ minWidth: 0 }}>
          <span style={lbl}>Company</span>
          <input style={inp} list={listId} value={stop.name} onChange={(e) => onCompany(e.target.value)} placeholder="type or pick saved…" />
          <datalist id={listId}>{directory.map((d) => <option key={d.key} value={d.name} />)}</datalist>
        </div>
        <div style={{ minWidth: 0 }}><span style={lbl}>Phone</span><input style={inp} value={stop.phone} onChange={(e) => set('phone', e.target.value)} /></div>
      </div>
      <div style={{ marginTop: '6px' }}><span style={lbl}>Address</span>
        <AddressAutocomplete style={inp} value={stop.address}
          onChange={(v) => set('address', v)}
          onPickParts={(p) => onChange({ ...stop, address: p.street || p.description, city: p.city || stop.city, state: p.state || stop.state, zip: p.zip || stop.zip })} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '8px', marginTop: '6px' }}>
        <div><span style={lbl}>City</span><input style={inp} value={stop.city} onChange={(e) => set('city', e.target.value)} /></div>
        <div><span style={lbl}>State</span><input style={inp} value={stop.state} onChange={(e) => set('state', e.target.value)} /></div>
        <div><span style={lbl}>Zip</span><input style={inp} value={stop.zip} onChange={(e) => set('zip', e.target.value)} /></div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginTop: '6px' }}>
        <div style={{ minWidth: 0 }}><span style={lbl}>Appt early</span><input type="datetime-local" style={{ ...inp, minWidth: 0 }} value={stop.early} onChange={(e) => set('early', e.target.value)} /></div>
        <div style={{ minWidth: 0 }}><span style={lbl}>Appt late</span><input type="datetime-local" style={{ ...inp, minWidth: 0 }} value={stop.late} onChange={(e) => set('late', e.target.value)} /></div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px', marginTop: '6px' }}>
        <div><span style={lbl}>{isPickup ? 'Pickup #' : 'Ref / PO #'}</span><input style={inp} value={stop.refNumber} onChange={(e) => set('refNumber', e.target.value)} /></div>
        <div><span style={lbl}>Commodity</span><input style={inp} value={stop.commodity} onChange={(e) => set('commodity', e.target.value)} /></div>
        <div><span style={lbl}>Weight (lb)</span><input type="number" style={inp} value={stop.weight} onChange={(e) => set('weight', e.target.value)} /></div>
      </div>
      <div style={{ marginTop: '6px' }}><span style={lbl}>Stop notes / instructions</span><input style={inp} value={stop.notes} onChange={(e) => set('notes', e.target.value)} /></div>
      <div style={{ marginTop: '10px', textAlign: 'right' }}>
        <button onClick={onAddSame} style={{ background: isPickup ? '#f0fdf4' : '#fef2f2', color: isPickup ? '#15803d' : '#b91c1c', fontSize: '12px', fontWeight: 700, padding: '5px 12px', borderRadius: '6px', border: `1px dashed ${isPickup ? '#86efac' : '#fca5a5'}`, cursor: 'pointer' }}>
          + Extra {isPickup ? 'Pickup' : 'Delivery'}
        </button>
      </div>
    </div>
  );
}

export default function LoadsPage() {
  const [loads, setLoads] = useState([]);
  const [devices, setDevices] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [editing, setEditing] = useState(null); // load object or null
  const [etab, setEtab] = useState('Summary'); // active tab in the load editor
  const [customAcc, setCustomAcc] = useState(() => { try { return JSON.parse(localStorage.getItem('dd-custom-acc')) || []; } catch { return []; } });
  const [newAcc, setNewAcc] = useState({ code: '', desc: '' });
  const [settings, setSettings] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [statusFilter, setStatusFilter] = useState('All');
  const [search, setSearch] = useState('');
  const [directory, setDirectory] = useState([]);
  const [importOpen, setImportOpen] = useState(false);
  const [importText, setImportText] = useState('');
  const [importBusy, setImportBusy] = useState(false);
  const [detention, setDetention] = useState(null);
  const [showDetention, setShowDetention] = useState(false);

  const refresh = async () => setLoads(await getLoads());
  const refreshDirectory = async () => setDirectory(await getParties());

  useEffect(() => {
    refresh();
    refreshDirectory();
    getBillSettings().then(setSettings);
    getDetentionSettings().then(setDetention);
    getDevices().then(setDevices).catch(() => {});
    getDrivers().then(setDrivers).catch(() => {});
    const t = setInterval(refresh, 30000); // pick up automation status changes
    return () => clearInterval(t);
  }, []);

  const saveProfile = async (party) => {
    if (!party.name && !party.account) { alert('Enter a company name or account number first.'); return; }
    await rememberParty(party);
    await refreshDirectory();
    alert(`Saved "${party.name || party.account}" to the directory.`);
  };

  const openNew = () => { playOpen(); setEtab('Summary'); setEditing(emptyLoad()); };

  const runImport = () => {
    if (!importText.trim()) { alert('Paste the rate confirmation text, or upload a PDF first.'); return; }
    const parsed = parseRateCon(importText);
    const base = emptyLoad();
    const sh = parsed.shipper || {}; const co = parsed.consignee || {};
    const puRef = (parsed.references || []).find((r) => r.type === 'Pickup#');
    const poRef = (parsed.references || []).find((r) => r.type === 'PO');

    // map the parsed freight onto the first pickup + first delivery stop
    const pickupStop = {
      ...emptyStop('pickup'),
      name: sh.name || '', address: sh.address || '', city: sh.city || '', state: sh.state || '', zip: sh.zip || '',
      early: (parsed.pickup || {}).early || '', late: (parsed.pickup || {}).late || '',
      refNumber: puRef ? puRef.number : '', commodity: parsed.commodity || '', weight: parsed.weight || '',
    };
    const deliveryStop = {
      ...emptyStop('delivery'),
      name: co.name || '', address: co.address || '', city: co.city || '', state: co.state || '', zip: co.zip || '',
      early: (parsed.delivery || {}).early || '', late: (parsed.delivery || {}).late || '',
      refNumber: poRef ? poRef.number : '', commodity: parsed.commodity || '',
    };

    const load = {
      ...base,
      stops: [pickupStop, deliveryStop],
      // broker → caller, and the broker's own load number
      caller: { ...base.caller, name: parsed.brokerName || '' },
      brokerLoadNumber: parsed.brokerLoadNumber || '',
      temp: parsed.temp || '',
      pieces: parsed.pieces || '', commodity: parsed.commodity || '', weight: parsed.weight || '',
      references: parsed.references || [],
      charges: parsed.charges || [],
      billing: { ...base.billing, poNumber: poRef ? poRef.number : '' },
      documents: { ...base.documents, rateCon: true }, // rate con is in hand
    };
    setImportOpen(false);
    setImportText('');
    setEtab('Summary');
    playOpen();
    setEditing(load); // opens the tabbed form pre-filled from the rate con, ready to review/adjust
  };

  const importPdf = async (file) => {
    if (!file) return;
    setImportBusy(true);
    try {
      const text = await pdfToText(file);
      if (!text.trim()) throw new Error('No selectable text found (the PDF may be a scan/image).');
      setImportText(text);
    } catch (e) {
      alert(`Couldn't read that PDF: ${e.message}\n\nTip: open the rate con, select all the text (Cmd+A), copy, and paste it into the box instead.`);
    }
    setImportBusy(false);
  };
  const openEdit = (l) => { playOpen(); setEtab('Summary'); setEditing(ensureStops({ ...l })); };
  const close = () => { playClose(); setEditing(null); };

  // add an accessorial as a charge line and jump to the Charges tab to fill the rate
  const addAccessorial = (code, desc) => {
    setEditing((ld) => ({ ...ld, charges: [...(ld.charges || []), { code, description: desc, qty: 1, rate: '' }] }));
    setEtab('Charges');
  };
  // create a new (reusable) charge category, remember it, and add it
  const addCustomAcc = () => {
    const desc = newAcc.desc.trim();
    if (!desc) { alert('Enter a name for the charge category.'); return; }
    const code = (newAcc.code.trim() || desc.split(/\s+/).map((w) => w[0]).join('').toUpperCase()).slice(0, 8);
    if (!customAcc.some((c) => c[0] === code && c[1] === desc)) {
      const next = [...customAcc, [code, desc]];
      setCustomAcc(next);
      localStorage.setItem('dd-custom-acc', JSON.stringify(next));
    }
    setNewAcc({ code: '', desc: '' });
    addAccessorial(code, desc);
  };
  const removeCustomAcc = (code, desc) => {
    const next = customAcc.filter((c) => !(c[0] === code && c[1] === desc));
    setCustomAcc(next);
    localStorage.setItem('dd-custom-acc', JSON.stringify(next));
  };

  // moves a driver onto a unit (with cumulative per-unit logs, same as the Drivers tab)
  const assignDriverToUnit = async (driverId, deviceId) => {
    const drv = drivers.find((d) => String(d.id) === String(driverId));
    if (!drv) return;
    const now = new Date().toISOString();
    const A = { ...(drv.attributes || {}) };
    const assignments = JSON.parse(JSON.stringify(A.assignments || {}));
    if (A.currentUnit && assignments[A.currentUnit]) {
      const openLog = assignments[A.currentUnit].logs.find((l) => !l.end);
      if (openLog) openLog.end = now;
    }
    const dev = devices.find((d) => String(d.id) === String(deviceId));
    if (!assignments[deviceId]) assignments[deviceId] = { name: dev ? dev.name : `Unit ${deviceId}`, logs: [] };
    assignments[deviceId].logs.push({ start: now, end: null });
    const updated = { ...drv, attributes: { ...A, assignments, currentUnit: deviceId } };
    await updateDriver(updated);
    setDrivers((ds) => ds.map((d) => (d.id === drv.id ? updated : d)));
  };

  const save = async () => {
    // derive shipper/consignee/schedule from the stop list
    const load = syncStops(editing);
    const stops = load.stops || [];
    if (!stops.some((s) => s.name)) { alert('Enter at least one stop with a company name.'); return; }

    // if a driver + vehicle are set, check whether that unit already has a different driver
    if (editing.driverId && editing.vehicleId) {
      const existing = drivers.find(
        (d) => String((d.attributes || {}).currentUnit) === String(editing.vehicleId) && String(d.id) !== String(editing.driverId)
      );
      const newDrv = drivers.find((d) => String(d.id) === String(editing.driverId));
      if (existing && newDrv) {
        const ok = window.confirm(
          `${existing.name} is currently assigned to ${vehName(editing.vehicleId)}.\n\n` +
          `Do you want to reassign this unit to ${newDrv.name}?\n\n` +
          `${existing.name}'s log on this unit will be closed, and ${newDrv.name} will take over.`
        );
        if (!ok) return; // dispatcher cancelled — leave everything as-is
        await assignDriverToUnit(editing.driverId, editing.vehicleId);
      } else if (newDrv && String((newDrv.attributes || {}).currentUnit) !== String(editing.vehicleId)) {
        // unit was free (or driver moving to a new unit) — assign quietly
        await assignDriverToUnit(editing.driverId, editing.vehicleId);
      }
    }

    // remember caller + every stop in the directory for future autofill
    await Promise.all([rememberParty(load.caller), ...stops.map((s) => rememberParty(s))]);
    await saveLoad(load);
    setEditing(null);
    playClose();
    refresh();
    refreshDirectory();
  };

  const vehName = (id) => (devices.find((d) => String(d.id) === String(id)) || {}).name;
  const drvName = (id) => (drivers.find((d) => String(d.id) === String(id)) || {}).name;

  const visible = loads.filter((l) => {
    if (statusFilter !== 'All' && l.status !== statusFilter) return false;
    if (!search.trim()) return true;
    const hay = `${l.billNumber} ${l.shipper.name} ${l.consignee.name} ${l.caller.name} ${vehName(l.vehicleId) || ''} ${drvName(l.driverId) || ''} ${(l.references || []).map((r) => r.number).join(' ')}`.toLowerCase();
    return hay.includes(search.trim().toLowerCase());
  });

  return (
    <div className="page-container">
      <div className="page-header" style={{ display: 'flex', alignItems: 'flex-end', gap: '16px', flexWrap: 'wrap' }}>
        <div style={{ flex: 1 }}>
          <h1>📦 Dispatch Board</h1>
          <p>Enter dispatch and billing information. Bill numbers are generated automatically.</p>
        </div>
        <button style={{ ...inp, width: 'auto', cursor: 'pointer', fontWeight: 600 }} onClick={() => setShowSettings(true)}>⚙ Bill #</button>
        <button style={{ ...inp, width: 'auto', cursor: 'pointer', fontWeight: 600 }} onClick={() => setShowDetention(true)}>⏱ Detention</button>
        <button style={btnBlue} onClick={() => { playOpen(); setImportOpen(true); }}>📄 Import Rate Con</button>
        <button style={btnOrange} onClick={openNew}>+ New Load</button>
      </div>

      {/* import rate confirmation */}
      {importOpen && (
        <div onClick={() => setImportOpen(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(10,26,47,0.6)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: 'white', borderRadius: '16px', width: 'min(680px, 96vw)', boxSizing: 'border-box', padding: '22px' }}>
            <h2 style={{ margin: '0 0 4px', fontFamily: 'Poppins, sans-serif', fontSize: '19px' }}>📄 Import Rate Confirmation</h2>
            <p style={{ fontSize: '13px', color: '#64748b', margin: '0 0 14px' }}>
              Upload the rate con PDF, or paste its text. We'll extract the load details and pre-fill a new load for you to review.
            </p>
            <div style={{ display: 'flex', gap: '10px', marginBottom: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
              <label style={{ ...btnBlue, cursor: 'pointer', display: 'inline-block' }}>
                {importBusy ? 'Reading…' : '⬆ Upload PDF'}
                <input type="file" accept=".pdf,.txt" style={{ display: 'none' }} onChange={(e) => { const f = e.target.files[0]; e.target.value = ''; if (f && f.type === 'text/plain') { f.text().then(setImportText); } else { importPdf(f); } }} />
              </label>
              <span style={{ fontSize: '12px', color: '#94a3b8' }}>or paste the text below</span>
            </div>
            <textarea
              value={importText}
              onChange={(e) => setImportText(e.target.value)}
              placeholder="Paste the rate confirmation text here…"
              style={{ ...inp, minHeight: '200px', fontFamily: 'monospace', fontSize: '12px', boxSizing: 'border-box' }}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '14px' }}>
              <button style={{ background: '#e2e8f0', color: '#334155', padding: '9px 18px', borderRadius: '8px', fontWeight: 700 }} onClick={() => setImportOpen(false)}>Cancel</button>
              <button style={btnOrange} onClick={runImport}>Extract &amp; review →</button>
            </div>
          </div>
        </div>
      )}

      {/* filters */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '14px', flexWrap: 'wrap', alignItems: 'center' }}>
        <select style={{ ...inp, width: '160px' }} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option>All</option>
          {LOAD_STATUSES.map((s) => <option key={s}>{s}</option>)}
        </select>
        <input style={{ ...inp, flex: 1, minWidth: '220px' }} placeholder="🔍 Search bill #, shipper, consignee, driver, reference…" value={search} onChange={(e) => setSearch(e.target.value)} />
      </div>

      {/* board */}
      <div className="card" style={{ padding: 0, overflowX: 'auto' }}>
        <table style={{ margin: 0 }}>
          <thead>
            <tr>
              <th>Bill #</th><th>Status</th><th>Shipper → Consignee</th><th>Pickup</th><th>Delivery</th>
              <th>Vehicle</th><th>Driver</th><th style={{ textAlign: 'right' }}>Total</th><th></th>
            </tr>
          </thead>
          <tbody>
            {visible.map((l) => (
              <tr key={l.id} style={{ cursor: 'pointer' }} onClick={() => openEdit(l)}>
                <td style={{ fontWeight: 700, color: '#0066ff' }}>{l.billNumber}</td>
                <td><StatusPill s={l.status} /></td>
                <td>
                  <div style={{ fontWeight: 600 }}>{l.shipper.name || '—'} {l.shipper.city && <span style={{ color: '#94a3b8', fontWeight: 400 }}>({l.shipper.city}, {l.shipper.state})</span>}</div>
                  <div style={{ color: '#64748b', fontSize: '12px' }}>→ {l.consignee.name || '—'} {l.consignee.city && `(${l.consignee.city}, ${l.consignee.state})`}</div>
                  {(l.stops || []).length > 2 && <div style={{ fontSize: '11px', color: '#f97316', fontWeight: 700 }}>⋯ {(l.stops || []).filter((s) => s.type === 'pickup').length} pickups · {(l.stops || []).filter((s) => s.type === 'delivery').length} deliveries</div>}
                </td>
                <td style={{ fontSize: '12px' }}>{l.pickup.early ? new Date(l.pickup.early).toLocaleDateString() : '—'}</td>
                <td style={{ fontSize: '12px' }}>{l.delivery.early ? new Date(l.delivery.early).toLocaleDateString() : '—'}</td>
                <td style={{ fontSize: '12px' }}>{vehName(l.vehicleId) || '—'}</td>
                <td style={{ fontSize: '12px' }}>{drvName(l.driverId) || '—'}</td>
                <td style={{ textAlign: 'right', fontWeight: 700 }}>{money(loadTotal(l))}</td>
                <td onClick={(e) => { e.stopPropagation(); if (window.confirm(`Delete load ${l.billNumber}?`)) { deleteLoad(l.id).then(refresh); } }} style={{ color: '#b91c1c', textAlign: 'center' }}>🗑</td>
              </tr>
            ))}
            {visible.length === 0 && <tr><td colSpan={9} style={{ textAlign: 'center', color: '#94a3b8', padding: '30px' }}>No loads yet — click “+ New Load”.</td></tr>}
          </tbody>
        </table>
      </div>

      {/* detention settings */}
      {showDetention && detention && (
        <div onClick={() => setShowDetention(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(10,26,47,0.55)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: 'white', borderRadius: '14px', padding: '22px', width: 'min(440px, 96vw)', boxSizing: 'border-box' }}>
            <h3 style={{ margin: '0 0 4px', fontFamily: 'Poppins, sans-serif' }}>⏱ Detention & Auto-Tracking</h3>
            <p style={{ fontSize: '12px', color: '#64748b', margin: '0 0 14px' }}>Each stop is geofenced. Detention starts <b>{detention.freeHours} hours after that stop's appointment time</b> and is billed <b>separately per stop</b> so you can see the amount for each.</p>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', marginBottom: '12px', fontWeight: 600 }}>
              <input type="checkbox" checked={detention.autoTrack} onChange={(e) => setDetention({ ...detention, autoTrack: e.target.checked })} />
              Enable automatic geofence tracking
            </label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
              <div><span style={lbl}>Free hours after appt</span><input type="number" style={inp} value={detention.freeHours} onChange={(e) => setDetention({ ...detention, freeHours: Number(e.target.value) || 0 })} /></div>
              <div><span style={lbl}>Rate / hour ($)</span><input type="number" style={inp} value={detention.ratePerHour} onChange={(e) => setDetention({ ...detention, ratePerHour: Number(e.target.value) || 0 })} /></div>
              <div><span style={lbl}>Geofence radius (m)</span><input type="number" style={inp} value={detention.radiusMeters} onChange={(e) => setDetention({ ...detention, radiusMeters: Number(e.target.value) || 300 })} /></div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '16px' }}>
              <button style={{ background: '#e2e8f0', color: '#334155', padding: '8px 14px', borderRadius: '6px', fontWeight: 600 }} onClick={() => setShowDetention(false)}>Cancel</button>
              <button style={btnBlue} onClick={async () => { await saveDetentionSettings(detention); setShowDetention(false); }}>Save</button>
            </div>
          </div>
        </div>
      )}

      {/* bill-number settings */}
      {showSettings && settings && (
        <div onClick={() => setShowSettings(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(10,26,47,0.55)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: 'white', borderRadius: '14px', padding: '22px', width: '360px' }}>
            <h3 style={{ margin: '0 0 12px', fontFamily: 'Poppins, sans-serif' }}>Bill Number Format</h3>
            <div style={{ display: 'flex', gap: '10px' }}>
              <div style={{ flex: 1 }}><span style={lbl}>Prefix</span><input style={inp} value={settings.prefix} onChange={(e) => setSettings({ ...settings, prefix: e.target.value })} /></div>
              <div style={{ flex: 1 }}><span style={lbl}>Next number</span><input type="number" style={inp} value={settings.next} onChange={(e) => setSettings({ ...settings, next: Number(e.target.value) || 1 })} /></div>
            </div>
            <p style={{ fontSize: '12px', color: '#64748b', margin: '8px 0 14px' }}>Next bill will be <b>{settings.prefix}{settings.next}</b>.</p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <button style={{ background: '#e2e8f0', color: '#334155', padding: '8px 14px', borderRadius: '6px', fontWeight: 600 }} onClick={() => setShowSettings(false)}>Cancel</button>
              <button style={btnBlue} onClick={async () => { await saveBillSettings(settings); setShowSettings(false); }}>Save</button>
            </div>
          </div>
        </div>
      )}

      {/* load editor */}
      {editing && (
        <div onClick={close} style={{ position: 'fixed', inset: 0, background: 'rgba(10,26,47,0.6)', zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '16px', overflowY: 'auto' }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: 'white', borderRadius: '16px', width: 'min(880px, 96vw)', boxSizing: 'border-box', padding: '20px', margin: 'auto' }}>
            {/* header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '16px' }}>
              <div>
                <h2 style={{ margin: 0, fontFamily: 'Poppins, sans-serif', fontSize: '20px', color: '#0f172a' }}>
                  {editing.billNumber ? `Load ${editing.billNumber}` : 'New Load'}
                </h2>
                {editing.createdAt && <div style={{ fontSize: '11px', color: '#94a3b8' }}>Created {fmt(editing.createdAt)}</div>}
                {!editing.billNumber && settings && <div style={{ fontSize: '11px', color: '#94a3b8' }}>Bill # will be {settings.prefix}{settings.next}</div>}
              </div>
              <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <select style={{ ...inp, width: 'auto' }} value={editing.status} onChange={(e) => setEditing({ ...editing, status: e.target.value })}>
                  {LOAD_STATUSES.map((s) => <option key={s}>{s}</option>)}
                </select>
                <button onClick={close} style={{ background: '#f1f5f9', borderRadius: '50%', width: '30px', height: '30px', padding: 0, fontSize: '16px', color: '#475569' }}>×</button>
              </div>
            </div>

            {/* parties */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '10px', marginBottom: '8px' }}>
              <PartyBlock title="📞 Caller / Broker" party={editing.caller} onChange={(p) => setEditing({ ...editing, caller: p })} directory={directory} listId="dir-caller" onSaveProfile={() => saveProfile(editing.caller)} />
            </div>
            <div style={{ marginBottom: '12px', fontSize: '12px' }}>
              <span style={{ color: '#64748b', marginRight: '8px' }}>Bill To:</span>
              {['Caller', 'Shipper', 'Consignee', 'Other'].map((b) => (
                <label key={b} style={{ marginRight: '12px', cursor: 'pointer' }}>
                  <input type="radio" checked={editing.billTo === b} onChange={() => setEditing({ ...editing, billTo: b })} /> {b}
                </label>
              ))}
            </div>

            {/* ===== TAB BAR ===== */}
            <div style={{ display: 'flex', gap: '1px', overflowX: 'auto', borderBottom: '2px solid #e2e8f0', marginBottom: '14px' }}>
              {LOAD_TABS.map((t) => (
                <button key={t} onClick={() => setEtab(t)} style={{
                  background: etab === t ? '#eff6ff' : 'transparent', color: etab === t ? '#0047ff' : '#64748b',
                  border: 'none', borderBottom: etab === t ? '2px solid #0047ff' : '2px solid transparent',
                  padding: '8px 12px', fontSize: '12px', fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap', marginBottom: '-2px',
                }}>{t}</button>
              ))}
            </div>

            {/* ===== SUMMARY ===== */}
            {etab === 'Summary' && (() => {
              const rev = loadTotal(editing);
              const st = editing.settlement || {};
              const net = num(st.milesPaid) * num(st.cpm) + num(st.stopPay) + num(st.detentionPay) + num(st.layoverPay) + num(st.bonuses) - num(st.advances) - num(st.deductions);
              const costTotal = (editing.costs || []).reduce((s, c) => s + num(c.amount), 0) + net;
              const margin = rev - costTotal;
              const card = (label, val, color) => <div style={{ ...sec, marginBottom: 0, textAlign: 'center' }}><div style={lbl}>{label}</div><div style={{ fontFamily: 'Poppins, sans-serif', fontWeight: 800, fontSize: '18px', color }}>{val}</div></div>;
              return (
                <div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '10px', marginBottom: '14px' }}>
                    {card('Revenue', money(rev), '#15803d')}
                    {card('Cost', money(costTotal), '#b91c1c')}
                    {card('Margin', money(margin), margin >= 0 ? '#15803d' : '#b91c1c')}
                    {card('Margin %', `${rev ? (margin / rev * 100).toFixed(1) : '0.0'}%`, '#0f172a')}
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px', marginBottom: '12px' }}>
                    <div style={sec}><div style={lbl}>Shipper</div><div style={{ fontSize: '13px', fontWeight: 700 }}>{editing.shipper && editing.shipper.city ? `${editing.shipper.name || ''} · ${editing.shipper.city}, ${editing.shipper.state}` : '—'}</div></div>
                    <div style={sec}><div style={lbl}>Consignee</div><div style={{ fontSize: '13px', fontWeight: 700 }}>{editing.consignee && editing.consignee.city ? `${editing.consignee.name || ''} · ${editing.consignee.city}, ${editing.consignee.state}` : '—'}</div></div>
                    <div style={sec}><div style={lbl}>Status</div><div style={{ marginTop: '2px' }}><StatusPill s={editing.status} /></div></div>
                  </div>
                  <div><span style={lbl}>Notes</span><textarea style={{ ...inp, minHeight: '54px' }} value={editing.notes} onChange={(e) => setEditing({ ...editing, notes: e.target.value })} /></div>
                </div>
              );
            })()}

            {/* ===== STOPS ===== */}
            {etab === 'Stops' && (
            <div style={{ marginBottom: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                <div style={{ fontFamily: 'Poppins, sans-serif', fontWeight: 700, fontSize: '14px', color: '#0f172a', flex: 1 }}>📍 Stops <span style={{ fontWeight: 400, fontSize: '12px', color: '#94a3b8' }}>({(editing.stops || []).filter((s) => s.type === 'pickup').length} pickup · {(editing.stops || []).filter((s) => s.type === 'delivery').length} delivery)</span></div>
                <button style={{ background: '#f0fdf4', color: '#15803d', fontSize: '12px', fontWeight: 700, padding: '6px 12px', borderRadius: '6px', marginRight: '6px' }} onClick={() => setEditing({ ...editing, stops: [...(editing.stops || []), emptyStop('pickup')] })}>+ Pickup</button>
                <button style={{ background: '#fef2f2', color: '#b91c1c', fontSize: '12px', fontWeight: 700, padding: '6px 12px', borderRadius: '6px' }} onClick={() => setEditing({ ...editing, stops: [...(editing.stops || []), emptyStop('delivery')] })}>+ Delivery</button>
              </div>
              {(() => {
                let pc = 0, dc = 0;
                return (editing.stops || []).map((s, i) => {
                  const n = s.type === 'pickup' ? ++pc : ++dc;
                  return (
                    <StopBlock key={s.id} stop={s} index={n} count={(editing.stops || []).length} directory={directory}
                      onChange={(next) => { const arr = [...editing.stops]; arr[i] = next; setEditing({ ...editing, stops: arr }); }}
                      onAddSame={() => { const arr = [...editing.stops]; arr.splice(i + 1, 0, emptyStop(s.type)); setEditing({ ...editing, stops: arr }); }}
                      onRemove={() => setEditing({ ...editing, stops: editing.stops.filter((_, j) => j !== i) })} />
                  );
                });
              })()}
            </div>
            )}

            {/* ===== DETAILS ===== */}
            {etab === 'Details' && (
            <div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '8px', marginBottom: '12px' }}>
              <div><span style={lbl}>Temp / reefer set point</span><input style={inp} placeholder="DRY or e.g. 34°F" value={editing.temp || ''} onChange={(e) => setEditing({ ...editing, temp: e.target.value })} /></div>
              <div><span style={lbl}>Pieces</span><input type="number" style={inp} value={editing.pieces} onChange={(e) => setEditing({ ...editing, pieces: e.target.value })} /></div>
              <div><span style={lbl}>Commodity</span><input style={inp} value={editing.commodity} onChange={(e) => setEditing({ ...editing, commodity: e.target.value })} /></div>
              <div style={{ gridColumn: 'span 2' }}><span style={lbl}>Description</span><input style={inp} value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} /></div>
              <div><span style={lbl}>Weight (lb)</span><input type="number" style={inp} value={editing.weight} onChange={(e) => setEditing({ ...editing, weight: e.target.value })} /></div>
              <div><span style={lbl}>Declared value</span><input type="number" style={inp} value={editing.declaredValue} onChange={(e) => setEditing({ ...editing, declaredValue: e.target.value })} /></div>
            </div>
            <div style={sec}>
              <div style={{ fontWeight: 700, fontSize: '13px', marginBottom: '8px' }}>🔖 Reference / Trace Numbers</div>
              {(editing.references || []).map((r, i) => (
                <div key={i} style={{ display: 'flex', gap: '8px', marginBottom: '6px' }}>
                  <select style={{ ...inp, width: '150px' }} value={r.type} onChange={(e) => { const refs = [...editing.references]; refs[i] = { ...r, type: e.target.value }; setEditing({ ...editing, references: refs }); }}>
                    {['Pickup#', 'PO', 'BOL', 'Ref', 'PRO'].map((t) => <option key={t}>{t}</option>)}
                  </select>
                  <input style={{ ...inp, flex: 1 }} placeholder="Number" value={r.number} onChange={(e) => { const refs = [...editing.references]; refs[i] = { ...r, number: e.target.value }; setEditing({ ...editing, references: refs }); }} />
                  <button style={{ background: '#fef2f2', color: '#b91c1c', padding: '0 12px', borderRadius: '6px', fontWeight: 700 }} onClick={() => setEditing({ ...editing, references: editing.references.filter((_, j) => j !== i) })}>×</button>
                </div>
              ))}
              <button style={{ ...btnBlue, padding: '6px 12px', fontSize: '12px' }} onClick={() => setEditing({ ...editing, references: [...(editing.references || []), { type: 'Pickup#', number: '' }] })}>+ Add reference</button>
            </div>
            </div>
            )}

            {/* ===== CHARGES ===== */}
            {etab === 'Charges' && (
            <div style={sec}>
              <div style={{ fontWeight: 700, fontSize: '13px', marginBottom: '8px' }}>💲 Charges (Linehaul & revenue)</div>
              {(editing.charges || []).map((c, i) => (
                <div key={i} style={{ display: 'grid', gridTemplateColumns: '110px 1fr 70px 90px 90px 34px', gap: '6px', marginBottom: '6px', alignItems: 'center' }}>
                  <input style={inp} placeholder="Code" value={c.code} onChange={(e) => { const ch = [...editing.charges]; ch[i] = { ...c, code: e.target.value }; setEditing({ ...editing, charges: ch }); }} />
                  <input style={inp} placeholder="Description" value={c.description} onChange={(e) => { const ch = [...editing.charges]; ch[i] = { ...c, description: e.target.value }; setEditing({ ...editing, charges: ch }); }} />
                  <input type="number" style={inp} placeholder="Qty" value={c.qty} onChange={(e) => { const ch = [...editing.charges]; ch[i] = { ...c, qty: e.target.value }; setEditing({ ...editing, charges: ch }); }} />
                  <input type="number" style={inp} placeholder="Rate" value={c.rate} onChange={(e) => { const ch = [...editing.charges]; ch[i] = { ...c, rate: e.target.value }; setEditing({ ...editing, charges: ch }); }} />
                  <div style={{ textAlign: 'right', fontWeight: 700, fontSize: '13px' }}>{money(chargeAmount(c))}</div>
                  <button style={{ background: '#fef2f2', color: '#b91c1c', borderRadius: '6px', fontWeight: 700 }} onClick={() => setEditing({ ...editing, charges: editing.charges.filter((_, j) => j !== i) })}>×</button>
                </div>
              ))}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '6px' }}>
                <button style={{ ...btnBlue, padding: '6px 12px', fontSize: '12px' }} onClick={() => setEditing({ ...editing, charges: [...(editing.charges || []), { code: '', description: '', qty: 1, rate: 0 }] })}>+ Add charge</button>
                <div style={{ fontFamily: 'Poppins, sans-serif', fontWeight: 700, fontSize: '16px' }}>Total: {money(loadTotal(editing))}</div>
              </div>
              <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                <div style={{ flex: 1 }}><span style={lbl}>Invoice type</span><input style={inp} value={editing.invoiceType} onChange={(e) => setEditing({ ...editing, invoiceType: e.target.value })} /></div>
                <div style={{ flex: 1 }}><span style={lbl}>Transaction type</span><input style={inp} value={editing.txType} onChange={(e) => setEditing({ ...editing, txType: e.target.value })} /></div>
              </div>
            </div>
            )}

            {/* ===== ACCESSORIALS ===== */}
            {etab === 'Accessorials' && (
            <div style={sec}>
              <div style={{ fontWeight: 700, fontSize: '13px', marginBottom: '8px' }}>➕ Accessorial charges</div>
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '10px' }}>
                {ACCESSORIAL_CODES.map(([code, desc]) => (
                  <button key={code} onClick={() => addAccessorial(code, desc)} style={{ background: '#eef2ff', color: '#1d4ed8', fontSize: '11px', fontWeight: 700, padding: '5px 9px', borderRadius: '6px', border: 'none', cursor: 'pointer' }}>+ {desc}</button>
                ))}
                {customAcc.map(([code, desc]) => (
                  <span key={code + desc} style={{ display: 'inline-flex', alignItems: 'center', background: '#ecfdf5', color: '#047857', fontSize: '11px', fontWeight: 700, borderRadius: '6px', overflow: 'hidden' }}>
                    <button onClick={() => addAccessorial(code, desc)} style={{ background: 'transparent', color: '#047857', fontSize: '11px', fontWeight: 700, padding: '5px 4px 5px 9px', border: 'none', cursor: 'pointer' }}>+ {desc}</button>
                    <button onClick={() => removeCustomAcc(code, desc)} title="Remove category" style={{ background: 'transparent', color: '#94a3b8', border: 'none', padding: '5px 7px 5px 2px', cursor: 'pointer', fontWeight: 700 }}>×</button>
                  </span>
                ))}
              </div>
              <p style={{ fontSize: '11px', color: '#94a3b8', margin: '0 0 10px' }}>Click a charge to add it and jump to Charges to set the rate. Auto-detention posts to Charges too.</p>
              <div style={{ borderTop: '1px dashed #e2e8f0', paddingTop: '10px' }}>
                <span style={lbl}>Create another charge category</span>
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                  <input style={{ ...inp, flex: '2 1 180px', minWidth: 0 }} placeholder="Charge name (e.g. Fumigation)" value={newAcc.desc} onChange={(e) => setNewAcc({ ...newAcc, desc: e.target.value })} onKeyDown={(e) => { if (e.key === 'Enter') addCustomAcc(); }} />
                  <input style={{ ...inp, flex: '1 1 90px', minWidth: 0 }} placeholder="Code (opt.)" value={newAcc.code} onChange={(e) => setNewAcc({ ...newAcc, code: e.target.value })} onKeyDown={(e) => { if (e.key === 'Enter') addCustomAcc(); }} />
                  <button style={{ ...btnBlue, padding: '7px 14px', fontSize: '12px' }} onClick={addCustomAcc}>+ Add category</button>
                </div>
              </div>
            </div>
            )}

            {/* ===== COST ===== */}
            {etab === 'Cost' && (
            <div style={sec}>
              <div style={{ fontWeight: 700, fontSize: '13px', marginBottom: '8px' }}>🧾 Cost details (internal)</div>
              {(editing.costs || []).map((c, i) => (
                <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 100px 110px 34px', gap: '6px', marginBottom: '6px', alignItems: 'center' }}>
                  <input style={inp} placeholder="Cost type (Driver pay, Fuel…)" value={c.type || ''} onChange={(e) => { const arr = [...editing.costs]; arr[i] = { ...c, type: e.target.value }; setEditing({ ...editing, costs: arr }); }} />
                  <input style={inp} placeholder="Vendor" value={c.vendor || ''} onChange={(e) => { const arr = [...editing.costs]; arr[i] = { ...c, vendor: e.target.value }; setEditing({ ...editing, costs: arr }); }} />
                  <input type="number" style={inp} placeholder="Amount" value={c.amount || ''} onChange={(e) => { const arr = [...editing.costs]; arr[i] = { ...c, amount: e.target.value }; setEditing({ ...editing, costs: arr }); }} />
                  <select style={inp} value={c.payStatus || 'Unpaid'} onChange={(e) => { const arr = [...editing.costs]; arr[i] = { ...c, payStatus: e.target.value }; setEditing({ ...editing, costs: arr }); }}>{['Unpaid', 'Approved', 'Paid'].map((s) => <option key={s}>{s}</option>)}</select>
                  <button style={{ background: '#fef2f2', color: '#b91c1c', borderRadius: '6px', fontWeight: 700 }} onClick={() => setEditing({ ...editing, costs: editing.costs.filter((_, j) => j !== i) })}>×</button>
                </div>
              ))}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '6px' }}>
                <button style={{ ...btnBlue, padding: '6px 12px', fontSize: '12px' }} onClick={() => setEditing({ ...editing, costs: [...(editing.costs || []), { type: '', vendor: '', amount: '', payStatus: 'Unpaid' }] })}>+ Add cost</button>
                <div style={{ fontWeight: 700, fontSize: '15px' }}>Cost total: {money((editing.costs || []).reduce((s, c) => s + num(c.amount), 0))}</div>
              </div>
            </div>
            )}

            {/* ===== DRIVER PAY / SETTLEMENT ===== */}
            {etab === 'Driver Pay' && (() => {
              const st = editing.settlement || {};
              const setS = (k, v) => setEditing({ ...editing, settlement: { ...(editing.settlement || {}), [k]: v } });
              const net = num(st.milesPaid) * num(st.cpm) + num(st.stopPay) + num(st.detentionPay) + num(st.layoverPay) + num(st.bonuses) - num(st.advances) - num(st.deductions);
              const F = (label, k) => <div><span style={lbl}>{label}</span><input type="number" style={inp} value={st[k] || ''} onChange={(e) => setS(k, e.target.value)} /></div>;
              return (
                <div style={sec}>
                  <div style={{ fontWeight: 700, fontSize: '13px', marginBottom: '8px' }}>👤 Driver settlement</div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '8px' }}>
                    {F('Miles paid', 'milesPaid')}{F('CPM rate', 'cpm')}{F('Stop pay', 'stopPay')}{F('Detention pay', 'detentionPay')}
                    {F('Layover pay', 'layoverPay')}{F('Bonuses', 'bonuses')}{F('Advances', 'advances')}{F('Deductions', 'deductions')}
                  </div>
                  <div style={{ textAlign: 'right', fontFamily: 'Poppins, sans-serif', fontWeight: 800, fontSize: '17px', marginTop: '10px', color: '#0f172a' }}>Net settlement: {money(net)}</div>
                </div>
              );
            })()}

            {/* ===== DOCUMENTS ===== */}
            {etab === 'Documents' && (
            <div style={sec}>
              <div style={{ fontWeight: 700, fontSize: '13px', marginBottom: '8px' }}>📎 Billing documents</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
                {DOC_LIST.map(([k, label]) => (
                  <label key={k} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#334155', background: 'white', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '8px 10px', cursor: 'pointer' }}>
                    <input type="checkbox" checked={!!(editing.documents || {})[k]} onChange={(e) => setEditing({ ...editing, documents: { ...(editing.documents || {}), [k]: e.target.checked } })} /> {label}
                  </label>
                ))}
              </div>
            </div>
            )}

            {/* ===== INVOICE ===== */}
            {etab === 'Invoice' && (() => {
              const b = editing.billing || {};
              const setB = (k, v) => setEditing({ ...editing, billing: { ...(editing.billing || {}), [k]: v } });
              const bc = billingChecklist(editing);
              return (
                <div>
                <div style={{ ...sec, background: bc.ready ? '#f0fdf4' : '#fffbeb', border: `1px solid ${bc.ready ? '#bbf7d0' : '#fde68a'}` }}>
                  <div style={{ fontWeight: 800, fontSize: '13px', marginBottom: '8px', color: bc.ready ? '#15803d' : '#b45309' }}>
                    {bc.ready ? '✅ Ready to bill — everything is in' : `📋 Ready-to-bill check — ${bc.missing.length} item${bc.missing.length === 1 ? '' : 's'} missing`}
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px' }}>
                    {bc.items.map((it) => (
                      <div key={it.key} style={{ fontSize: '12px', color: it.ok ? '#15803d' : '#b45309', display: 'flex', gap: '6px', alignItems: 'center' }}>
                        <span>{it.ok ? '✓' : '○'}</span> {it.label}
                      </div>
                    ))}
                  </div>
                  <p style={{ fontSize: '11px', color: '#94a3b8', margin: '8px 0 0' }}>Check off documents on the Documents tab (or log the driver's POD/lumper reply). {bc.hadLumper ? 'This load had a lumper — a revised rate con from the broker is required.' : ''}</p>
                </div>
                <div style={sec}>
                  <div style={{ fontWeight: 700, fontSize: '13px', marginBottom: '8px' }}>🧾 Invoice</div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '8px' }}>
                    <div><span style={lbl}>Invoice #</span><input style={inp} value={b.invoiceNumber || ''} onChange={(e) => setB('invoiceNumber', e.target.value)} /></div>
                    <div><span style={lbl}>Invoice date</span><input type="date" style={inp} value={b.invoiceDate || ''} onChange={(e) => setB('invoiceDate', e.target.value)} /></div>
                    <div><span style={lbl}>Due date</span><input type="date" style={inp} value={b.dueDate || ''} onChange={(e) => setB('dueDate', e.target.value)} /></div>
                    <div><span style={lbl}>Terms</span><select style={inp} value={b.terms || 'Net 30'} onChange={(e) => setB('terms', e.target.value)}>{['Net 15', 'Net 30', 'Net 45', 'Net 60', 'Due on receipt'].map((t) => <option key={t}>{t}</option>)}</select></div>
                    <div><span style={lbl}>Customer PO #</span><input style={inp} value={b.poNumber || ''} onChange={(e) => setB('poNumber', e.target.value)} /></div>
                    <div><span style={lbl}>Billing status</span><select style={inp} value={b.status || 'Open'} onChange={(e) => setB('status', e.target.value)}>{['Open', 'Ready to Bill', 'Invoiced', 'Paid', 'Closed'].map((s) => <option key={s}>{s}</option>)}</select></div>
                    <div><span style={lbl}>Sales rep</span><input style={inp} value={b.salesRep || ''} onChange={(e) => setB('salesRep', e.target.value)} /></div>
                    <div><span style={lbl}>Branch / terminal</span><input style={inp} value={b.branch || ''} onChange={(e) => setB('branch', e.target.value)} /></div>
                  </div>
                </div>
                </div>
              );
            })()}

            {/* ===== A/R ===== */}
            {etab === 'A/R' && (() => {
              const b = editing.billing || {};
              const setB = (k, v) => setEditing({ ...editing, billing: { ...(editing.billing || {}), [k]: v } });
              const rev = loadTotal(editing);
              const paid = num(b.paymentsReceived);
              return (
                <div style={sec}>
                  <div style={{ fontWeight: 700, fontSize: '13px', marginBottom: '8px' }}>💰 Accounts Receivable</div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '8px', marginBottom: '10px' }}>
                    <div><span style={lbl}>Invoice amount</span><input style={{ ...inp, background: '#f1f5f9' }} value={money(rev)} readOnly /></div>
                    <div><span style={lbl}>Payments received</span><input type="number" style={inp} value={b.paymentsReceived || ''} onChange={(e) => setB('paymentsReceived', e.target.value)} /></div>
                    <div><span style={lbl}>Balance due</span><input style={{ ...inp, background: '#f1f5f9', fontWeight: 700 }} value={money(rev - paid)} readOnly /></div>
                    <div><span style={lbl}>Dispute status</span><select style={inp} value={b.disputeStatus || 'None'} onChange={(e) => setB('disputeStatus', e.target.value)}>{['None', 'Open dispute', 'Resolved'].map((s) => <option key={s}>{s}</option>)}</select></div>
                  </div>
                  <div><span style={lbl}>Collection notes</span><textarea style={{ ...inp, minHeight: '48px' }} value={b.collectionNotes || ''} onChange={(e) => setB('collectionNotes', e.target.value)} /></div>
                </div>
              );
            })()}

            {/* ===== DISPATCH ===== */}
            {etab === 'Dispatch' && (
            <div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px', marginBottom: '16px' }}>
              <div><span style={lbl}>🚛 Assign vehicle</span>
                <select style={inp} value={editing.vehicleId} onChange={(e) => setEditing({ ...editing, vehicleId: e.target.value })}>
                  <option value="">— none —</option>
                  {devices.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
              </div>
              <div><span style={lbl}>👤 Assign driver</span>
                <select style={inp} value={editing.driverId} onChange={(e) => setEditing({ ...editing, driverId: e.target.value })}>
                  <option value="">— none —</option>
                  {drivers.map((d) => <option key={d.id} value={d.id}>{d.name} ({d.uniqueId})</option>)}
                </select>
              </div>
              <div><span style={lbl}>Trip number</span><input style={inp} value={editing.tripNumber} onChange={(e) => setEditing({ ...editing, tripNumber: e.target.value })} /></div>
            </div>
            <div style={sec}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                <span style={{ fontWeight: 700, fontSize: '13px' }}>🛰️ Auto-Tracking</span>
                <label style={{ fontSize: '12px', marginLeft: 'auto', cursor: 'pointer' }}>
                  <input type="checkbox" checked={editing.autoTrack} onChange={(e) => setEditing({ ...editing, autoTrack: e.target.checked })} /> Track this load automatically
                </label>
              </div>
              {!editing.vehicleId && <p style={{ fontSize: '12px', color: '#94a3b8', margin: 0 }}>Assign a vehicle to enable geofence tracking.</p>}
              {editing.vehicleId && (
                <div style={{ fontSize: '12px', color: '#475569', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 16px' }}>
                  <span>Geofences: <b>{editing.geo && editing.geo.armed ? 'armed ✓' : 'will arm shortly'}</b></span>
                  <span>Pickup arrival: <b>{editing.track && editing.track.pickupArrival ? new Date(editing.track.pickupArrival).toLocaleString() : '—'}</b></span>
                  <span>Pickup detention: <b>{editing.track ? (editing.track.pickupDetentionHrs || 0).toFixed(1) : '0'} hr</b></span>
                  <span>Delivery arrival: <b>{editing.track && editing.track.deliveryArrival ? new Date(editing.track.deliveryArrival).toLocaleString() : '—'}</b></span>
                  <span>Delivery detention: <b>{editing.track ? (editing.track.deliveryDetentionHrs || 0).toFixed(1) : '0'} hr</b></span>
                </div>
              )}
            </div>
            </div>
            )}

            {/* ===== AUDIT ===== */}
            {etab === 'Audit' && (
            <div style={sec}>
              <div style={{ fontWeight: 700, fontSize: '13px', marginBottom: '8px' }}>🕑 Audit / status history</div>
              {(editing.statusHistory || []).length === 0 && <p style={{ fontSize: '12px', color: '#94a3b8', margin: 0 }}>No changes recorded yet.</p>}
              {(editing.statusHistory || []).slice().reverse().map((h, i) => (
                <div key={i} style={{ display: 'flex', gap: '10px', fontSize: '12.5px', padding: '5px 0', borderBottom: '1px solid #eef2f7' }}>
                  <span style={{ color: '#94a3b8', minWidth: '150px' }}>{fmt(h.at)}</span>
                  <span style={{ fontWeight: 700, color: '#0f172a' }}>{h.status}</span>
                  {h.note && <span style={{ color: '#64748b' }}>· {h.note}</span>}
                </div>
              ))}
            </div>
            )}

            {/* ===== MARGIN ===== */}
            {etab === 'Margin' && (() => {
              const rev = loadTotal(editing);
              const st = editing.settlement || {};
              const net = num(st.milesPaid) * num(st.cpm) + num(st.stopPay) + num(st.detentionPay) + num(st.layoverPay) + num(st.bonuses) - num(st.advances) - num(st.deductions);
              const costTotal = (editing.costs || []).reduce((s, c) => s + num(c.amount), 0) + net;
              const margin = rev - costTotal;
              const row = (label, val, bold) => <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #eef2f7', fontWeight: bold ? 800 : 400, fontSize: bold ? '15px' : '13px' }}><span>{label}</span><span>{val}</span></div>;
              return (
                <div style={sec}>
                  <div style={{ fontWeight: 700, fontSize: '13px', marginBottom: '8px' }}>📊 Margin analysis</div>
                  {row('Revenue', money(rev))}
                  {row('Driver settlement', money(net))}
                  {row('Other cost', money((editing.costs || []).reduce((s, c) => s + num(c.amount), 0)))}
                  {row('Total cost', money(costTotal))}
                  {row('Gross margin', money(margin), true)}
                  {row('Margin %', `${rev ? (margin / rev * 100).toFixed(1) : '0.0'}%`, true)}
                </div>
              );
            })()}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '16px' }}>
              <button style={{ background: '#e2e8f0', color: '#334155', padding: '9px 18px', borderRadius: '8px', fontWeight: 700 }} onClick={close}>Cancel</button>
              <button style={btnBlue} onClick={save}>{editing.billNumber ? 'Save changes' : 'Create load'}</button>
            </div>
            <p style={{ fontSize: '11px', color: '#94a3b8', marginTop: '10px' }}>Loads are stored in this browser for now — they move to the server when the dashboard is deployed.</p>
          </div>
        </div>
      )}
    </div>
  );
}
